<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import FilterBar from '@/components/FilterBar.ce.vue';
    import PackageDetail from '@/components/packages/PackageDetail.ce.vue';
    import PackageForm from '@/components/packages/PackageForm.ce.vue';
    import PackagesList from '@/components/packages/PackagesList.ce.vue';
    import Shop from '@/components/quote/shop/Shop.ce.vue';
    import SpaceSelection from '@/components/quote/space/SpaceSelection.ce.vue';
    import DateTimeSelector from '@/components/ui/DateTimeSelector.ce.vue';
    import SubmitConfirmation from '@/components/ui/SubmitConfirmation.ce.vue';
    import { PACKAGE_STEP_MAP, WIDGET_MODULES } from '@/constants/tracking';
    import { getProductCategories, getProducts, getSpaces } from '@/helpers/axios';
    import { type PackageTyping } from '@/typings/widget';
    import { type IdTyping } from '@shared/typings';
    import { type Ref } from 'vue';

    const props = defineProps<{
        venue: string
        locale: string
        maxGuestAmount?: number
        minGuestAmount?: number
        guestAmountStep?: number
        hideFilter?: boolean
        hideTitle?: boolean
        inPath?: boolean
        comingFromMenu?: boolean
        isOpen?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'scrollToTop' | 'backToMain' | 'processRequest' | 'backToPath'): void
        (event: 'toggleHome', value: boolean): void
        (event: 'filteredPackages', value: number): void
    }>();

    const { t } = useI18n();

    // Stores
    const businessStore = useWidgetBusinessStore();
    const { resetBusinessStore } = businessStore;
    const {
        venue: venueData,
        venueSpaces,
        venueProducts,
        venueProductCategories,
        filteredVenueProductCategories,
    } = storeToRefs(useWidgetVenueStore());
    const { spaceIds, productIds } = storeToRefs(businessStore);
    const { eventTypeId } = storeToRefs(useWidgetPathStore());
    const trackingStore = useWidgetTrackingStore();
    const {
        eventTypeFilter,
        guestAmountFilter,
        startDateFilter,
        startTimeFilter,
    } = storeToRefs(useWidgetFilterStore());

    // Helpers
    const getFilteredProductsByCategory = (categoryId: IdTyping): typeof venueProducts.value => {
        // First, filter product by category id
        const categoryFilteredProducts = venueProducts.value.filter(product => product.categories.some(cat => cat.id === categoryId));

        // Then, filter products by selected event type
        return categoryFilteredProducts.filter(product => product.eventTypes.some(eventType => eventType.id === eventTypeId.value));
    };

    // Refs
    const packageListView: Ref<boolean> = ref(false);
    const packageView: Ref<boolean>= ref(false);
    const formView: Ref<boolean> = ref(false);
    const confirmationView: Ref<boolean> = ref(false);
    const dateView: Ref<boolean> = ref(false);
    const spaceView: Ref<boolean> = ref(false);
    const productView: Ref<boolean> = ref(false);
    const packageItem: Ref<PackageTyping> = ref({} as PackageTyping);
    const isOnBusinessPath: Ref<boolean> = ref(false);

    // Computed
    const filteredPackages: ComputedRef<PackageTyping[]> = computed(() => {
        if (eventTypeFilter.value) {
            return (venueData.value?.packages ?? []).filter(pkg => pkg.eventTypes.map(eventType => eventType.id)
                .includes(Number(eventTypeFilter.value)));
        }
        return venueData.value?.packages ?? [];
    });
    const spacesForEventType: ComputedRef<typeof venueSpaces.value> = computed(() =>
        venueSpaces.value.filter(space => space.eventTypes.some(eventType => eventType.id === eventTypeId.value)));
    const productsForEventType: ComputedRef<typeof venueProducts.value> = computed(() =>
        venueProducts.value.filter(product => product.eventTypes.some(eventType => eventType.id === eventTypeId.value)));
    const categoriesWithProducts: ComputedRef<typeof filteredVenueProductCategories.value> = computed(() =>
        filteredVenueProductCategories.value.filter(category => getFilteredProductsByCategory(category.id).length > 0));

    /*
     * Analytics: resolve the single active package screen so each one can emit a step_viewed.
     * Steps are only tracked for the standalone packages module — when `inPath` the journey
     * ('quote') owns the funnel.
     */
    const activePackageView: ComputedRef<string | null> = computed(() => {
        if (confirmationView.value) return 'confirmationView';
        if (formView.value) return 'formView';
        if (productView.value) return 'productView';
        if (spaceView.value) return 'spaceView';
        if (dateView.value) return 'dateView';
        if (packageView.value) return 'packageView';
        if (packageListView.value) return 'packageListView';
        return null;
    });

    watch(activePackageView, (key: string | null) => {
        /*
         * Track the business/package steps whenever they are part of an active flow:
         * - in-path (the "make your own package" branch of the journey or self-quote) — always;
         * - standalone — once the visitor has committed to the packages module.
         * On the home screen (no active module, not in-path) the persistent package list is skipped.
         */
        if (!key) return;
        if (!props.inPath && trackingStore.activeModule !== WIDGET_MODULES.PACKAGES) return;

        const descriptor = PACKAGE_STEP_MAP[key];
        if (descriptor) trackingStore.trackStepViewed(descriptor);
    });

    // Methods
    const openPackageItem = (selectedItem: PackageTyping):void => {
        packageItem.value = selectedItem;
        packageListView.value = false;
        packageView.value = true;

        // Analytics: opening a package is the visitor committing to the packages module.
        if (!props.inPath) trackingStore.trackModuleStarted(WIDGET_MODULES.PACKAGES);
    };

    const handleGetProductCategories = async(): Promise<void> => {
        try {
            venueProductCategories.value = await getProductCategories(props.venue, props.locale);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Backend call failed', err);
        }
    };

    const handleGetProducts = async() :Promise<void> => {
        try {
            venueProducts.value = await getProducts(props.venue, props.locale);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Backend call failed', err);
        }
    };

    const handleGetSpaces = async(): Promise<void> => {
        try {
            venueSpaces.value = await getSpaces(props.venue, props.locale);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading spaces:', error);
        }
    };

    const handleRequest = ():void => {
        formView.value = false;
        confirmationView.value = true;
        emit('processRequest');
    };

    const handleSelectDates = (): void => {
        packageListView.value = false;
        isOnBusinessPath.value = true;
        dateView.value = true;

        // Analytics: starting the business (date-first) flow is also a commit to the packages module.
        if (!props.inPath) trackingStore.trackModuleStarted(WIDGET_MODULES.PACKAGES);
    };

    const handleSelectSpaces = () : void => {
        dateView.value = false;
        spaceView.value = true;
    };

    const handleSelectProducts = () : void => {
        spaceView.value = false;
        productView.value = true;
    };

    const handleOverview = () : void => {
        productView.value = false;
        packageView.value = true;
    };

    const resetProps = (): void => {
        packageView.value = false;
        formView.value = false;
        dateView.value = false;
        spaceView.value = false;
        productView.value = false;
        confirmationView.value = false;
        isOnBusinessPath.value = false;
        eventTypeFilter.value = '';
        guestAmountFilter.value = 0;
        startDateFilter.value = '';
        startTimeFilter.value = '';
        packageListView.value = true;
    };

    // eslint-disable-next-line max-statements, complexity, max-lines-per-function
    const goBack = ():void => {
        // If is in date view, go back to package list
        if (dateView.value) {
            resetProps();
            resetBusinessStore();
            return;
        }

        // If is in space view, go back to date view
        if (spaceView.value) {
            spaceView.value = false;
            dateView.value = true;
            return;
        }

        // If is in product view, go back to space view
        if (productView.value) {
            productView.value = false;
            spaceView.value = true;
            return;
        }

        // If is in form view, go back to package view
        if (formView.value) {
            formView.value = false;
            packageView.value = true;
            return;
        }

        // If is in Package view but not in path, go back to package list
        if (packageView.value && !isOnBusinessPath.value) {
            resetProps();
            resetBusinessStore();
            return;
        }

        // If is in Package view but in path, go back to products
        if (packageView.value && isOnBusinessPath.value) {
            packageView.value = false;
            productView.value = true;
        }
    };

    const backToPrevious = (): void => {
        resetProps();
        if (props.inPath) emit('backToPath');
        if (props.comingFromMenu) emit('backToMain');
    };

    const backToMain = (): void => {
        resetProps();
        emit('backToMain');
    };

    // Hooks
    onMounted(async() => {
        packageListView.value = true;
        if (props.inPath) {
            await Promise.allSettled([
                handleGetProductCategories(),
                handleGetProducts(),
                handleGetSpaces(),
            ]);
        }
    });

    onBeforeUnmount(() => {
        resetBusinessStore();
    });

    // Watchers
    watch([
        packageListView,
        packageView,
        formView,
        confirmationView,
        dateView,
        spaceView,
        productView,
    ], (newVals, oldVals) => {
        if (newVals.some((val, i) => val && !oldVals[i])) emit('scrollToTop');
    });

    watch(() => packageListView.value, nVal => {
        if (nVal && !props.comingFromMenu) emit('toggleHome', true);
    });

    watch(() => packageView.value, nVal => {
        if (nVal && !props.comingFromMenu) emit('toggleHome', false);
    });

    watch(() => filteredPackages.value, nVal => {
        if (nVal) emit('filteredPackages', nVal.length);
    });

    watch(() => props.isOpen, nVal => {
        if (nVal) resetProps();
    });

    watch(() => eventTypeFilter.value, nVal => {
        if (nVal) eventTypeId.value = Number(nVal);
    });
</script>

<template>
    <div class="package-selection">
        <div
            v-if="packageListView"
            class="package-selection__list"
        >
            <div
                v-if="inPath || comingFromMenu"
                class="package-selection__back-btn"
            >
                <BackButton @click="backToPrevious" />
            </div>
            <h2 v-if="!hideTitle">
                {{ t('widget.titles.ourPackages') }}
            </h2>
            <FilterBar
                v-show="!hideFilter"
                :max-guest-amount="maxGuestAmount"
                :min-guest-amount="minGuestAmount"
                :guest-amount-step="guestAmountStep"
            />
            <PackagesList
                :packages="filteredPackages"
                :in-path="inPath"
                @package-selected="openPackageItem"
                @create-own="handleSelectDates"
            />
        </div>
        <div
            v-else
            class="package-selection__form"
        >
            <!-- Date and Time Selection -->
            <DateTimeSelector
                v-if="dateView"
                @go-back="goBack"
                @next="handleSelectSpaces"
            />

            <!-- Space Selection -->
            <SpaceSelection
                v-if="spaceView"
                v-model="spaceIds"
                :items="spacesForEventType"
                :is-in-path="inPath"
                @next="handleSelectProducts"
                @go-back="goBack"
            />

            <!-- Product Selection -->
            <Shop
                v-if="productView"
                v-model="productIds"
                :products="productsForEventType"
                :categories="categoriesWithProducts"
                :is-in-path="inPath"
                @next="handleOverview"
                @go-back="goBack"
            />

            <!-- Package Detail -->
            <PackageDetail
                v-if="packageView"
                :package-item="packageItem"
                :is-on-business-path="isOnBusinessPath"
                :max-guest-amount="maxGuestAmount"
                :min-guest-amount="minGuestAmount"
                :guest-amount-step="guestAmountStep"
                @show-book-view="packageView = false; formView = true"
                @go-back="goBack"
                @show-spaces="packageView = false; spaceView = true;"
                @show-products="packageView = false; productView = true;"
            />

            <!-- Package Form Request -->
            <PackageForm
                v-if="formView"
                :venue="venue"
                :package-id="isOnBusinessPath ? '' : packageItem.id"
                :locale="locale"
                @process-request="handleRequest"
                @scroll-to-top="emit('scrollToTop')"
                @go-back="goBack"
            />

            <!-- Success Page -->
            <SubmitConfirmation
                v-if="confirmationView"
                @back-to-main="backToMain"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.package-selection {
    position: relative;
    z-index: var(--zindex-popup);
    display: flex;
    flex: 1;
    background-color: rgb(237 237 237);
    inline-size: 100%;
    min-block-size: 100%;

    &__list,
    &__form {
        flex: 1;

        // Allow these flex items to shrink so wide children (e.g. the product shop grid)
        // resize to the available width instead of overflowing the x-axis
        min-inline-size: 0;
    }

    &__list {
        flex: 1;

        h2 {
            font-size: var(--font-h1);
            margin-block-start: var(--spacing-04);
            text-align: center;
        }
    }

    &__form {
        inline-size: 100%;
        min-block-size: 100%;
    }

    &__back-btn {
        line-height: 1;
        margin-block: var(--spacing-04);
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }
}
</style>
